import PropTypes from 'prop-types';
import React from 'react';
import StripTags from 'striptags';

import { Image } from '$partials/media';
import { Tag } from '$partials/ui';
import { useLanguage } from '$contexts/language.jsx';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const ProjectLargeItem = ({
    ariaLabel         = null,
    ariaPosinset      = null,
    ariaSetsize       = null,
    ariaTabIndex      = null,
    client            = null,
    headingImageLarge = null,
    headingText       = null,
    listingImageLarge = null,
    namespace,
    tagAll            = null,
    title             = null,
    url               = null,
}) => {
    // hooks
    const { translate: t } = useLanguage();

    // misc
    const image = listingImageLarge || headingImageLarge || null;

    let HeadingElement = '';
    if(namespace?.includes('homepage')) {
        HeadingElement = 'h3';
    } else {
        HeadingElement = 'h2';
    }

    // render
    return (
        <article
            aria-label={ariaLabel}
            aria-posinset={ariaPosinset}
            aria-setsize={ariaSetsize}
            className='overflow-hidden'
            tabIndex={ariaTabIndex}>
            <div className='relative flex flex-col justify-center hover-image-scale hover-reveal'>
                <div className='project-large-card__panel col-12 sm-absolute sm-push-left-3 relative z2'>
                    <div className='col-12 sm-col-6 bg-lemon-yellow-crayola border-rad-10 py7'>
                        <div className='push-left-2 sm-push-left-3'>
                            <div className='clearfix mxn-gutter'>
                                <div className='col col-11 md-col-10 lg-col-10 px-gutter'>
                                    <div className='clearfix mxn3 mb4'>
                                        <div className='col col-4 sm-col-3 type-style-5 px3 capitalise'>
                                            {t('Work')}
                                        </div>

                                        {client && (
                                            <div className='col col-4 sm-col-5 type-style-5 px3'>
                                                {client}
                                            </div>
                                        )}
                                    </div>

                                    <a
                                        className='link-reset block'
                                        href={url}>
                                        <HeadingElement>
                                            <div
                                                className='rich-text type-style-3'
                                                dangerouslySetInnerHTML={{ __html: StripTags(headingText || title, ['mark', 'a','h2', 'h3', 'h4', 'hr', 'br', 'b', 'i']) }} />
                                        </HeadingElement>
                                    </a>
                                </div>
                            </div>

                            {tagAll?.edges?.length > 0 && (
                                <div className='md-show relative hover-reveal-target-400'>
                                    <ul className='list-reset flex flex-wrap pt6 pr6'>
                                        {tagAll.edges.map((tag, i) => {
                                            // only show 2 tags
                                            if (i > 2) { return null; }

                                            return (
                                                <li
                                                    className='mb2 mr2'
                                                    key={i}>
                                                    <Tag {...tag.node} />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* image */}
                {image && (
                    <a
                        className='link-reset block'
                        href={url}>
                        <div className='border-rad-10 mtn4 sm-mt0 relative z1'>
                            <Image
                                {...image}
                                containerClassName='hover-image-scale-target'
                                height={960}
                                width={1440} />
                        </div>
                    </a>
                )}
            </div>
        </article>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

ProjectLargeItem.propTypes = {
    ariaLabel:         PropTypes.string,
    ariaPosinset:      PropTypes.number,
    ariaSetsize:       PropTypes.number,
    ariaTabIndex:      PropTypes.number,
    client:            PropTypes.string,
    headingImageLarge: PropTypes.object,
    headingText:       PropTypes.string,
    listingImageLarge: PropTypes.object,
    namespace:         PropTypes.string,
    tagAll:            PropTypes.object,
    title:             PropTypes.string,
    url:               PropTypes.string,
};

ProjectLargeItem.__typename = 'ProjectLarge';

export { ProjectLargeItem };

export default ProjectLargeItem;
